import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { http } from "../../shared/api/http.js";
import type { MediaItem } from "../../shared/types.js";
import type { ThemeId } from "@smartvow/shared";

export interface GuestEvent {
  tokenValid: boolean;
  event: {
    id: string;
    name: string;
    slug: string;
    date: string;
    location: string | null;
    theme: ThemeId;
    revealAt: string;
    captureStartAt: string | null;
    captureEndAt: string | null;
    captureState: "before" | "open" | "closed";
    wallEnabled: boolean;
  };
  wedding: { partner1Name: string; partner2Name: string; story: string | null };
  couplePhotoUrls: string[];
}

interface GalleryPage {
  locked: boolean;
  revealAt: string | null;
  items: MediaItem[];
  nextCursor: string | null;
}

/* -- jeton invité : indexé par slug (propre à un événement) -- */
export const tokenStore = {
  save: (slug: string, token: string) => localStorage.setItem(`sv:token:${slug}`, token),
  load: (slug: string) => localStorage.getItem(`sv:token:${slug}`) ?? undefined,
};

/* -- identité invité (nom + email) : GLOBALE, saisie une seule fois puis réutilisée -- */
export interface GuestIdentity {
  name: string;
  email: string;
}
const ID_KEY = "sv:guest:identity";
export const identityStore = {
  save: (v: GuestIdentity) =>
    localStorage.setItem(
      ID_KEY,
      JSON.stringify({ name: v.name.trim(), email: v.email.trim().toLowerCase() }),
    ),
  load: (): GuestIdentity => {
    try {
      const raw = JSON.parse(localStorage.getItem(ID_KEY) ?? "{}");
      return { name: raw.name ?? "", email: raw.email ?? "" };
    } catch {
      return { name: "", email: "" };
    }
  },
  clear: () => localStorage.removeItem(ID_KEY),
};

/** Inscription (nom + email) à un événement — envoie un email de bienvenue une fois. */
export const registerGuest = (
  slug: string,
  token: string,
  body: { name: string; email: string },
) =>
  http.post<{ ok: true; emailed: boolean; revealAt: string }>(
    `/guest/events/${slug}/register?t=${encodeURIComponent(token)}`,
    body,
  );

export const useGuestEvent = (slug: string, token?: string) =>
  useQuery({
    queryKey: ["guest", slug, token],
    queryFn: () =>
      http.get<GuestEvent>(
        `/guest/events/${slug}${token ? `?t=${encodeURIComponent(token)}` : ""}`,
      ),
  });

/** Pagine automatiquement (comme le mur) : la galerie ne doit jamais plafonner à la 1re page. */
export const useGuestGallery = (eventId: string | undefined, token: string | undefined) =>
  useInfiniteQuery({
    queryKey: ["gallery", "guest", eventId, token],
    enabled: !!eventId && !!token,
    refetchInterval: 15_000,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ t: token! });
      if (pageParam) q.set("cursor", pageParam);
      return http.get<GalleryPage>(`/events/${eventId}/gallery?${q}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/** Les médias envoyés par cet invité (identifié par email), tous statuts, hors verrou. */
export const useMySubmissions = (
  eventId: string | undefined,
  email: string,
  token: string | undefined,
) =>
  useQuery({
    queryKey: ["my-media", eventId, email],
    enabled: !!eventId && !!token && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email),
    refetchInterval: 10_000,
    queryFn: () =>
      http.get<{ items: MediaItem[] }>(
        `/events/${eventId}/my-media?email=${encodeURIComponent(email)}&t=${encodeURIComponent(token!)}`,
      ),
  });
